import Link from "next/link";
import Image from "next/image";
import type { Metadata } from "next";
import { HardHat, HelpCircle, Building2, Wrench, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const metadata: Metadata = {
  title: "FAQ — RateRight",
  description:
    "Frequently asked questions about RateRight, the simplest way to hire construction workers in Australia.",
};

export const dynamic = "force-static";

const workerFaqs = [
  {
    q: "How does RateRight work for workers?",
    a: "RateRight connects qualified construction workers with contractors who need skilled labour. Simply create your profile, verify your qualifications, and get matched with jobs that fit your skills and schedule.",
  },
  {
    q: "Is RateRight free for workers?",
    a: "Yes! It is completely free for workers to join, create a profile, and get matched with jobs. We do not charge any subscription fees or take a percentage of your earnings.",
  },
  {
    q: "How do I get hired through RateRight?",
    a: "Complete your profile with accurate information about your skills, experience, and certifications. Upload your White Card and any trade-specific licences. Contractors search our database and contact workers directly.",
  },
  {
    q: "What trades does RateRight cover?",
    a: "We cover all major construction trades including carpentry, plumbing, electrical, bricklaying, concreting, tiling, plastering, painting, landscaping, and general labour.",
  },
  {
    q: "Do I need a White Card to join?",
    a: "Yes, all workers must have a valid White Card (General Construction Induction Card) to work on Australian construction sites.",
  },
  {
    q: "How quickly can I find work?",
    a: "Many workers receive their first job invitation within 24\u201348 hours of profile approval.",
  },
  {
    q: "How and when do I get paid?",
    a: "Payment terms are agreed directly with the contractor before starting work. Most contractors pay weekly or fortnightly. RateRight does not handle payments.",
  },
  {
    q: "Can I choose when and where I work?",
    a: "Absolutely! You set your availability and preferred work locations in your profile.",
  },
];

const contractorFaqs = [
  {
    q: "How does RateRight work for contractors?",
    a: "RateRight gives you instant access to pre-verified construction workers. Search our database by trade, location, availability, and ratings.",
  },
  {
    q: "What is the $50 fee about?",
    a: "We charge a flat $50 fee per successful job placement. No subscriptions, no hidden costs, no percentage cuts.",
  },
  {
    q: "How fast can I find workers?",
    a: "Most contractors find suitable workers within hours. Many jobs are filled the same day they are posted.",
  },
  {
    q: "What if the worker does not show up?",
    a: "We take reliability seriously. Workers with no-shows are removed from the platform. Contact us immediately and we will help you find a replacement quickly.",
  },
  {
    q: "Are workers verified?",
    a: "Yes, all workers go through our verification process. We check White Cards, trade licences, and conduct identity verification.",
  },
  {
    q: "How many workers can I contact?",
    a: "As many as you need! There is no limit to how many workers you can view or contact.",
  },
  {
    q: "Do I have to use RateRight for payment?",
    a: "No, you handle payment directly with the worker according to your normal payroll processes.",
  },
  {
    q: "What areas does RateRight cover?",
    a: "We currently operate in Sydney, Melbourne, Brisbane, Perth, and Adelaide metropolitan areas.",
  },
];

const generalFaqs = [
  {
    q: "Is RateRight a labour hire company?",
    a: "No, RateRight is a marketplace platform that connects contractors directly with workers. We are not a labour hire company.",
  },
  {
    q: "How is payment handled?",
    a: "Payment is arranged directly between the contractor and worker. RateRight does not process payments or take a percentage of wages.",
  },
  {
    q: "What areas do you cover?",
    a: "RateRight currently operates in major Australian cities: Sydney, Melbourne, Brisbane, Perth, and Adelaide.",
  },
  {
    q: "Is there a rating system?",
    a: "Yes, both workers and contractors can rate each other after completing a job.",
  },
  {
    q: "How do I get started?",
    a: "Simply visit our website and click Sign Up. Workers create a profile with their skills and qualifications. Contractors can start searching immediately.",
  },
  {
    q: "What makes RateRight different?",
    a: "Unlike traditional labour hire companies, we charge contractors a simple flat fee with no ongoing subscriptions. Workers keep 100% of their wages.",
  },
];

function FAQSection({
  title,
  icon,
  faqs,
  idPrefix,
}: {
  title: string;
  icon: React.ReactNode;
  faqs: { q: string; a: string }[];
  idPrefix: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
          {icon}
        </div>
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq, i) => (
          <AccordionItem key={i} value={`${idPrefix}-${i}`}>
            <AccordionTrigger>{faq.q}</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Orange accent bar */}
      <div className="h-1 bg-primary" />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/logo_black.svg"
              alt="RateRight"
              width={32}
              height={32}
              className="h-8 w-8"
            />
            <span className="text-xl font-bold tracking-tight">RateRight</span>
          </Link>
          <Link href="/auth/login">
            <Button variant="ghost" size="sm">
              Sign in
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-screen-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <HelpCircle className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Everything you need to know about hiring and finding work on
            RateRight.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="px-4 pb-16">
        <div className="mx-auto max-w-screen-sm space-y-10">
          <FAQSection
            title="For Workers"
            icon={<Wrench className="h-4 w-4 text-primary-foreground" />}
            faqs={workerFaqs}
            idPrefix="worker"
          />

          <FAQSection
            title="For Contractors"
            icon={<Building2 className="h-4 w-4 text-primary-foreground" />}
            faqs={contractorFaqs}
            idPrefix="contractor"
          />

          <FAQSection
            title="General Questions"
            icon={<Users className="h-4 w-4 text-primary-foreground" />}
            faqs={generalFaqs}
            idPrefix="general"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t px-4 py-12">
        <div className="mx-auto max-w-screen-sm text-center">
          <h2 className="text-xl font-bold tracking-tight">
            Still have questions?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Get in touch and we&apos;ll get back to you as soon as we can.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link href="/auth/signup">
              <Button className="w-full sm:w-auto h-12 gap-2 text-base font-semibold">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t px-4 py-8">
        <div className="mx-auto max-w-screen-sm text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2">
            <Image
              src="/logo_black.svg"
              alt="RateRight"
              width={24}
              height={24}
              className="h-6 w-6"
            />
            <span className="font-semibold text-foreground">RateRight</span>
          </div>
          <p className="mt-2">
            The simplest way to hire construction workers in Australia.
          </p>
          <p className="mt-4">
            &copy; {new Date().getFullYear()} RateRight Pty Ltd. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
